import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type PolicyApplyAdjustment,
  type PolicyPreviewRow,
  type PolicyPreviewRowsPage,
  clearPreferredPriceOverrides,
} from '../../api/pricing';
import {
  PolicySignalsPanel,
  type PolicyPriceDebug,
} from './PolicySignalsPanel';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';

type RowFilter = 'all' | 'increases' | 'decreases' | 'overrides' | 'needs_review';

type SortKey =
  | 'card'
  | 'set'
  | 'condition'
  | 'printing'
  | 'qty'
  | 'acq'
  | 'current'
  | 'proposed'
  | 'delta'
  | 'lineDelta'
  | 'absDelta';

type SortDir = 'asc' | 'desc';

interface RowEdit {
  applyPrice: string;
  skip: boolean;
}

export interface PolicyPreviewPanelProps {
  preview: PolicyPreviewRowsPage | null;
  previewReady: boolean;
  previewBusy: boolean;
  applyBusy: boolean;
  /** Job status summary — available before row fetch completes */
  statusSummary?: Record<string, unknown> | null;
  onPreview: () => void;
  onApply: (previewId: string, adjustments: PolicyApplyAdjustment[]) => void;
  onDiscard: () => void;
  /** Called after bulk override clear so parent can refresh preview summary */
  onOverridesCleared?: (message: string) => void;
}

const PAGE_SIZE = 75;

const ANOMALY_LABELS: Record<string, string> = {
  large_move: 'Large move',
  high_vs_market: 'High vs market',
  low_vs_market: 'Low vs market',
  no_low_comp: 'No low comp',
  outlier_rejected: 'Outlier dropped',
  signal_divergence: 'Signal divergence',
};

const FILTER_OPTIONS: { key: RowFilter; label: string; summaryKey?: string }[] = [
  { key: 'needs_review', label: 'Flagged', summaryKey: 'needs_review' },
  { key: 'all', label: 'All movers', summaryKey: 'would_stamp' },
  { key: 'increases', label: 'Increases', summaryKey: 'increases' },
  { key: 'decreases', label: 'Decreases', summaryKey: 'decreases' },
  { key: 'overrides', label: 'Overrides', summaryKey: 'override_holds' },
];

function money(value?: number | null): string {
  return value != null && Number.isFinite(Number(value)) ? `$${Number(value).toFixed(2)}` : '—';
}

function signedMoney(value?: number | null): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  if (n === 0) return '$0.00';
  const prefix = n > 0 ? '+' : '-';
  return `${prefix}$${Math.abs(n).toFixed(2)}`;
}

function parsePrice(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
}

function defaultApplyPrice(row: PolicyPreviewRow): string {
  if (row.proposed_policy_price == null) return '';
  return row.proposed_policy_price.toFixed(2);
}

function isMover(row: PolicyPreviewRow): boolean {
  return (
    row.proposed_policy_price != null &&
    row.current_policy_price !== row.proposed_policy_price
  );
}

function rowMatchesFilter(row: PolicyPreviewRow, filter: RowFilter): boolean {
  if (filter === 'all') return isMover(row);
  if (filter === 'increases') return isMover(row) && (row.policy_delta ?? 0) > 0;
  if (filter === 'decreases') return isMover(row) && (row.policy_delta ?? 0) < 0;
  if (filter === 'overrides') return Boolean(row.override_holds);
  if (filter === 'needs_review') return Boolean(row.needs_review);
  return true;
}

function buildAdjustments(
  rows: PolicyPreviewRow[],
  edits: Record<string, RowEdit>,
): PolicyApplyAdjustment[] {
  const out: PolicyApplyAdjustment[] = [];
  for (const row of rows) {
    const edit = edits[row.variant_id];
    if (!edit) continue;
    if (edit.skip) {
      out.push({ variant_id: row.variant_id, skip: true });
      continue;
    }
    const parsed = parsePrice(edit.applyPrice);
    if (
      parsed != null &&
      row.proposed_policy_price != null &&
      parsed !== row.proposed_policy_price
    ) {
      out.push({ variant_id: row.variant_id, proposed_policy_price: parsed });
    }
  }
  return out;
}

function isReviewResolved(
  row: PolicyPreviewRow,
  edit: RowEdit | undefined,
  acknowledged: Set<string>,
): boolean {
  if (!row.needs_review) return true;
  if (edit?.skip) return true;
  if (acknowledged.has(row.variant_id)) return true;
  const parsed = parsePrice(edit?.applyPrice ?? '');
  if (
    parsed != null &&
    row.proposed_policy_price != null &&
    parsed !== row.proposed_policy_price
  ) {
    return true;
  }
  return false;
}

function formatCurrentPolicy(row: PolicyPreviewRow): React.ReactNode {
  if (row.current_policy_price != null && Number.isFinite(Number(row.current_policy_price))) {
    return money(row.current_policy_price);
  }
  if (row.current_sell_base != null && Number.isFinite(Number(row.current_sell_base))) {
    return (
      <span style={styles.currentCell}>
        {money(row.current_sell_base)}
        <span style={styles.currentHint}>unstamped</span>
      </span>
    );
  }
  return <span style={styles.newStamp}>New</span>;
}

function rowQty(row: PolicyPreviewRow): number {
  const qty = Number(row.copy_count ?? 1);
  return Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 1;
}

function weightedDelta(row: PolicyPreviewRow): number {
  if (row.policy_delta_dollars != null && Number.isFinite(Number(row.policy_delta_dollars))) {
    return Number(row.policy_delta_dollars);
  }
  return (row.policy_delta ?? 0) * rowQty(row);
}

function formatAcquisitionCost(row: PolicyPreviewRow): string {
  const min = row.acquisition_cost_min;
  const max = row.acquisition_cost_max;
  if (max == null || !Number.isFinite(Number(max))) return '—';
  if (min != null && Number.isFinite(Number(min)) && min !== max) {
    return `${money(min)}–${money(max)}`;
  }
  return money(max);
}

function currentSortValue(row: PolicyPreviewRow): number {
  if (row.current_policy_price != null && Number.isFinite(Number(row.current_policy_price))) {
    return Number(row.current_policy_price);
  }
  if (row.current_sell_base != null && Number.isFinite(Number(row.current_sell_base))) {
    return Number(row.current_sell_base);
  }
  return Number.NEGATIVE_INFINITY;
}

function sortValue(row: PolicyPreviewRow, key: SortKey): string | number {
  switch (key) {
    case 'card':
      return (row.product_name ?? '').toLowerCase();
    case 'set':
      return (row.set_name ?? '').toLowerCase();
    case 'condition':
      return (row.condition ?? '').toLowerCase();
    case 'printing':
      return (row.printing ?? '').toLowerCase();
    case 'qty':
      return rowQty(row);
    case 'acq':
      return row.acquisition_cost_max != null && Number.isFinite(Number(row.acquisition_cost_max))
        ? Number(row.acquisition_cost_max)
        : Number.NEGATIVE_INFINITY;
    case 'current':
      return currentSortValue(row);
    case 'proposed':
      return row.proposed_policy_price != null && Number.isFinite(Number(row.proposed_policy_price))
        ? Number(row.proposed_policy_price)
        : Number.NEGATIVE_INFINITY;
    case 'delta':
      return Number(row.policy_delta ?? 0);
    case 'lineDelta':
      return weightedDelta(row);
    case 'absDelta':
      return Math.abs(Number(row.policy_delta ?? 0));
    default:
      return 0;
  }
}

function compareRows(a: PolicyPreviewRow, b: PolicyPreviewRow, key: SortKey, dir: SortDir): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  let cmp = 0;
  if (typeof av === 'string' && typeof bv === 'string') {
    cmp = av.localeCompare(bv);
  } else {
    cmp = Number(av) - Number(bv);
  }
  if (cmp === 0 && key !== 'card') {
    cmp = (a.product_name ?? '').localeCompare(b.product_name ?? '');
  }
  return dir === 'asc' ? cmp : -cmp;
}

export function PolicyPreviewPanel({
  preview,
  previewReady,
  previewBusy,
  applyBusy,
  statusSummary,
  onPreview,
  onApply,
  onDiscard,
  onOverridesCleared,
}: PolicyPreviewPanelProps) {
  const [filter, setFilter] = useState<RowFilter>('needs_review');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('absDelta');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [clearOverridesBusy, setClearOverridesBusy] = useState(false);
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [acknowledged, setAcknowledged] = useState<Set<string>>(() => new Set());
  const [selectedReview, setSelectedReview] = useState<Set<string>>(() => new Set());

  const previewId = preview?.preview_id ?? '';
  const allRows = preview?.rows ?? [];
  const summary = { ...(statusSummary ?? {}), ...(preview?.summary ?? {}) };
  const showPreviewChrome = previewReady || Boolean(preview);

  useEffect(() => {
    setEdits({});
    setAcknowledged(new Set());
    setSelectedReview(new Set());
    setSearch('');
    setPage(0);
    setSortKey('absDelta');
    setSortDir('desc');
    const needsReview = Number(summary.needs_review ?? 0);
    setFilter(needsReview > 0 ? 'needs_review' : 'all');
  }, [previewId, summary.needs_review]);

  useEffect(() => {
    if (!allRows.length) return;
    setEdits((prev) => {
      const next = { ...prev };
      for (const row of allRows) {
        if (!next[row.variant_id]) {
          next[row.variant_id] = { applyPrice: defaultApplyPrice(row), skip: false };
        }
      }
      return next;
    });
  }, [allRows]);

  useEffect(() => {
    setPage(0);
  }, [filter, search, sortKey, sortDir]);

  const reviewRows = useMemo(
    () => allRows.filter((row) => row.needs_review && isMover(row)),
    [allRows],
  );

  const unresolvedReview = useMemo(
    () =>
      reviewRows.filter((row) => !isReviewResolved(row, edits[row.variant_id], acknowledged)),
    [reviewRows, edits, acknowledged],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows
      .filter((row) => rowMatchesFilter(row, filter))
      .filter((row) => {
        if (!q) return true;
        const hay = `${row.product_name ?? ''} ${row.set_name ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [allRows, filter, search, sortKey, sortDir]);

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(
      key === 'card' || key === 'set' || key === 'condition' || key === 'printing'
        ? 'asc'
        : 'desc',
    );
  }, [sortKey]);

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return ' ⬍';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const renderSortableTh = (label: string, key: SortKey, align: 'left' | 'right' = 'left') => (
    <th style={{ ...styles.th, ...(align === 'right' ? styles.thNum : {}), ...styles.thSortable }}>
      <button
        type="button"
        style={{
          ...styles.sortButton,
          ...(align === 'right' ? { justifyContent: 'flex-end' } : {}),
        }}
        onClick={() => toggleSort(key)}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {sortIndicator(key)}
      </button>
    </th>
  );

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filteredRows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const pageDeltaTotals = useMemo(() => {
    let up = 0;
    let down = 0;
    for (const row of filteredRows) {
      const d = weightedDelta(row);
      if (d > 0) up += d;
      else if (d < 0) down += Math.abs(d);
    }
    return { up, down, net: up - down };
  }, [filteredRows]);

  const allReviewAcknowledged = unresolvedReview.length === 0;
  const canApply = previewReady && Boolean(previewId) && allReviewAcknowledged && !applyBusy;
  const canDiscard = showPreviewChrome && !previewBusy && !applyBusy;

  const acknowledgeAll = useCallback(() => {
    setAcknowledged((prev) => {
      const next = new Set(prev);
      for (const row of reviewRows) {
        next.add(row.variant_id);
      }
      return next;
    });
    setSelectedReview(new Set());
  }, [reviewRows]);

  const selectAllReview = useCallback(() => {
    setSelectedReview(new Set(reviewRows.map((row) => row.variant_id)));
  }, [reviewRows]);

  const acknowledgeSelected = useCallback(() => {
    if (!selectedReview.size) return;
    setAcknowledged((prev) => {
      const next = new Set(prev);
      for (const id of selectedReview) {
        next.add(id);
      }
      return next;
    });
    setSelectedReview(new Set());
  }, [selectedReview]);

  const toggleReviewSelect = (variantId: string) => {
    setSelectedReview((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) next.delete(variantId);
      else next.add(variantId);
      return next;
    });
  };

  const updateEdit = (variantId: string, patch: Partial<RowEdit>) => {
    setEdits((prev) => ({
      ...prev,
      [variantId]: { applyPrice: prev[variantId]?.applyPrice ?? '', skip: false, ...patch },
    }));
  };

  const renderAnomalyChips = (row: PolicyPreviewRow) => {
    const flags = row.anomalies ?? [];
    if (!flags.length) return null;
    return (
      <span style={styles.chipRow}>
        {flags.map((flag) => (
          <span key={flag} style={styles.chipWarn}>
            {ANOMALY_LABELS[flag] ?? flag}
          </span>
        ))}
      </span>
    );
  };

  const showReviewActions = filter === 'needs_review' && reviewRows.length > 0;

  const renderRow = (row: PolicyPreviewRow) => {
    const edit = edits[row.variant_id] ?? {
      applyPrice: defaultApplyPrice(row),
      skip: false,
    };
    const resolved = isReviewResolved(row, edit, acknowledged);
    const rowStyle = {
      ...styles.dataRow,
      ...(row.needs_review && !resolved ? styles.dataRowNeedsReview : {}),
      ...(edit.skip ? styles.dataRowSkipped : {}),
    };

    return (
      <tr key={row.variant_id} style={rowStyle}>
        {showReviewActions && (
          <td style={styles.td}>
            <input
              type="checkbox"
              checked={selectedReview.has(row.variant_id)}
              onChange={() => toggleReviewSelect(row.variant_id)}
              aria-label={`Select ${row.product_name ?? row.variant_id}`}
            />
          </td>
        )}
        <td style={styles.td}>
          <div style={styles.cardName}>{row.product_name ?? row.variant_id}</div>
          {(row.card_number || row.printing) && (
            <div style={styles.cardMeta}>
              {row.card_number ? `#${row.card_number}` : null}
              {row.card_number && row.printing ? ' · ' : null}
              {row.printing ?? null}
            </div>
          )}
          <div style={styles.cardMeta}>
            SKU {row.tcgplayer_sku_id ?? '—'}
            {row.tcgplayer_product_id ? ` · pid ${row.tcgplayer_product_id}` : ''}
          </div>
          {row.debug &&
          Boolean(
            row.debug.tcg_export_name ||
              row.debug.catalog_name ||
              row.debug.tcg_join ||
              row.debug.manapool_low_join,
          ) ? (
              <div style={styles.cardMeta}>
                {String(row.debug.tcg_join) === 'sku' && row.debug.tcg_export_name
                  ? `TCG: ${String(row.debug.tcg_export_name)}${
                      row.debug.tcg_export_set
                        ? ` (${String(row.debug.tcg_export_set)})`
                        : ''
                    }`
                  : `TCG join: ${String(row.debug.tcg_join ?? '—')}`}
                {row.debug.catalog_name
                  ? ` · MP: ${String(row.debug.catalog_name)}${
                      row.debug.catalog_set || row.debug.catalog_number
                        ? ` ${[row.debug.catalog_set, row.debug.catalog_number]
                            .filter(Boolean)
                            .map(String)
                            .join(' #')}`
                        : ''
                    }`
                  : ` · MP join: ${String(row.debug.manapool_low_join ?? '—')}`}
              </div>
            ) : null}
          {renderAnomalyChips(row)}
          {row.debug && (
            <PolicySignalsPanel
              compact
              policyPrice={row.proposed_policy_price}
              debug={row.debug as PolicyPriceDebug}
            />
          )}
        </td>
        <td style={styles.td}>{row.set_name ?? '—'}</td>
        <td style={styles.td}>{row.condition ?? '—'}</td>
        <td style={styles.td}>{row.printing ?? '—'}</td>
        <td style={styles.tdNum}>{rowQty(row)}</td>
        <td style={styles.tdNum}>{formatAcquisitionCost(row)}</td>
        <td style={styles.td}>{formatCurrentPolicy(row)}</td>
        <td style={styles.td}>
          <input
            type="text"
            inputMode="decimal"
            value={edit.applyPrice}
            disabled={edit.skip || applyBusy}
            onChange={(e) => updateEdit(row.variant_id, { applyPrice: e.target.value })}
            style={styles.priceInput}
            aria-label={`Apply price for ${row.product_name ?? row.variant_id}`}
          />
        </td>
        <td style={styles.td}>{signedMoney(row.policy_delta)}</td>
        <td style={styles.tdNum}>
          {rowQty(row) > 1 ? signedMoney(weightedDelta(row)) : '—'}
        </td>
        <td style={styles.td}>
          {money(row.current_sell_base)} → {money(row.proposed_sell_base)}
          {row.override_holds && (
            <span style={styles.chipInfo}> override {money(row.override_price)}</span>
          )}
        </td>
        <td style={styles.td}>
          <label style={styles.skipLabel}>
            <input
              type="checkbox"
              checked={edit.skip}
              disabled={applyBusy}
              onChange={(e) => updateEdit(row.variant_id, { skip: e.target.checked })}
            />
            Skip
          </label>
          {row.needs_review && !resolved && (
            <button
              type="button"
              style={styles.ackButton}
              disabled={applyBusy}
              onClick={() =>
                setAcknowledged((prev) => new Set(prev).add(row.variant_id))
              }
            >
              Ack
            </button>
          )}
        </td>
      </tr>
    );
  };

  const totalIncrease = summary.total_increase_dollars as number | undefined;
  const totalDecrease = summary.total_decrease_dollars as number | undefined;
  const netDelta = summary.net_policy_delta_dollars as number | undefined;
  const overrideCount = Number(summary.override_holds ?? 0);

  const clearAllOverrides = useCallback(async () => {
    if (clearOverridesBusy || applyBusy || previewBusy) return;
    const label =
      overrideCount > 0
        ? `${overrideCount.toLocaleString()} preferred-price override(s)`
        : 'all preferred-price overrides';
    if (
      !window.confirm(
        `Clear ${label}? This clears every preferred_price_override stamp (not only sellable). ` +
          'Sell base falls back to policy, listing, or preferred price. ' +
          'Channel prices update only where sell base actually changes.',
      )
    ) {
      return;
    }
    setClearOverridesBusy(true);
    try {
      const result = await clearPreferredPriceOverrides({ scope: 'all' });
      onOverridesCleared?.(result.message);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail ??
                'Failed to clear overrides',
            )
          : err instanceof Error
            ? err.message
            : 'Failed to clear overrides';
      onOverridesCleared?.(
        typeof message === 'string' && message.length ? `Failed: ${message}` : 'Failed to clear overrides',
      );
    } finally {
      setClearOverridesBusy(false);
    }
  }, [
    applyBusy,
    clearOverridesBusy,
    onOverridesCleared,
    overrideCount,
    previewBusy,
  ]);

  return (
    <div style={styles.wrap}>
      <div style={styles.actionRow}>
        <button
          type="button"
          style={{
            ...styles.primaryButton,
            ...(previewBusy ? styles.buttonDisabled : {}),
          }}
          disabled={previewBusy || applyBusy}
          onClick={onPreview}
        >
          {previewBusy ? 'Previewing…' : 'Preview Policy Delta'}
        </button>
        <button
          type="button"
          style={{
            ...styles.primaryButton,
            ...(!canApply ? styles.buttonDisabled : {}),
          }}
          disabled={!canApply}
          title={
            !previewReady
              ? 'Run preview first'
              : !allReviewAcknowledged
                ? `Acknowledge ${unresolvedReview.length} flagged row(s) first`
                : 'Stamp reviewed prices and fan out'
          }
          onClick={() => onApply(previewId, buildAdjustments(allRows, edits))}
        >
          {applyBusy ? 'Applying…' : 'Apply Preview'}
        </button>
        <button
          type="button"
          style={{
            ...styles.secondaryButton,
            ...(!canDiscard ? styles.buttonDisabled : {}),
          }}
          disabled={!canDiscard}
          title="Clear this preview without stamping. Run Preview again for a fresh delta."
          onClick={onDiscard}
        >
          Discard Preview
        </button>
      </div>

      {showPreviewChrome && (
        <>
          <div style={styles.summaryBar}>
            <div style={styles.summaryGrid}>
              <span>
                <strong>{String(summary.would_stamp ?? '—')}</strong> stamps ·{' '}
                <strong>{String(summary.sell_base_would_change ?? '—')}</strong> sell-base changes
              </span>
              <span>
                ↑ <strong>{String(summary.increases ?? '—')}</strong> (
                {money(totalIncrease)} inv) · ↓ <strong>{String(summary.decreases ?? '—')}</strong>{' '}
                ({money(totalDecrease)} inv) · net <strong>{signedMoney(netDelta)}</strong>
              </span>
            {overrideCount > 0 && (
              <span>
                Overrides holding sell base: <strong>{overrideCount.toLocaleString()}</strong>
                {' · '}
                <button
                  type="button"
                  style={{
                    ...styles.linkButton,
                    ...(clearOverridesBusy || previewBusy || applyBusy
                      ? styles.buttonDisabled
                      : {}),
                  }}
                  disabled={clearOverridesBusy || previewBusy || applyBusy}
                  title="Clear preferred_price_override on all variants"
                  onClick={() => void clearAllOverrides()}
                >
                  {clearOverridesBusy ? 'Clearing…' : 'Clear all overrides'}
                </button>
              </span>
            )}
              {Number(summary.needs_review ?? 0) > 0 && (
                <span style={unresolvedReview.length ? styles.reviewPending : styles.reviewOk}>
                  Flagged: {unresolvedReview.length} of {String(summary.needs_review)} remaining
                </span>
              )}
            </div>
          </div>

          <div style={styles.toolbarFixed}>
            <div style={styles.filterRow}>
              {FILTER_OPTIONS.map(({ key, label, summaryKey }) => {
                const count =
                  summaryKey != null && summary[summaryKey] != null
                    ? String(summary[summaryKey])
                    : null;
                return (
                  <button
                    key={key}
                    type="button"
                    style={{
                      ...styles.filterButton,
                      ...(filter === key ? styles.filterButtonActive : {}),
                    }}
                    onClick={() => setFilter(key)}
                  >
                    {label}
                    {count != null ? ` (${count})` : ''}
                  </button>
                );
              })}
              <input
                type="search"
                placeholder="Search card or set…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            {showReviewActions && (
              <div style={styles.reviewActions}>
                <span style={styles.reviewHint}>
                  Click column headers to sort
                  {sortKey === 'absDelta' && sortDir === 'desc' ? ' (default: largest |Δ|)' : ''}
                </span>
                <button type="button" style={styles.secondaryButton} onClick={selectAllReview}>
                  Select all flagged
                </button>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  disabled={!selectedReview.size}
                  onClick={acknowledgeSelected}
                >
                  Ack selected ({selectedReview.size})
                </button>
                <button type="button" style={styles.primarySmall} onClick={acknowledgeAll}>
                  Acknowledge all flagged
                </button>
              </div>
            )}

            {filteredRows.length > 0 ? (
              <div style={styles.pageMeta}>
                <span>
                  Showing {safePage * PAGE_SIZE + 1}–
                  {Math.min((safePage + 1) * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
                  {filter !== 'all' ? ` (${filter.replace('_', ' ')})` : ' movers'}
                  {pageDeltaTotals.net !== 0 && (
                    <>
                      {' '}
                      · filter net {signedMoney(pageDeltaTotals.net)} inv (↑{money(pageDeltaTotals.up)}{' '}
                      ↓{money(pageDeltaTotals.down)})
                    </>
                  )}
                </span>
                <div style={styles.pageControls}>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    disabled={safePage <= 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Prev
                  </button>
                  <span style={styles.pageLabel}>
                    Page {safePage + 1} / {pageCount}
                  </span>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    disabled={safePage >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.emptyFilter}>
                {allRows.length === 0
                  ? 'Loading preview rows…'
                  : 'No rows match this filter or search.'}
              </div>
            )}
          </div>

          {filteredRows.length > 0 && (
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {showReviewActions && <th style={styles.th} />}
                    {renderSortableTh('Card', 'card')}
                    {renderSortableTh('Set', 'set')}
                    {renderSortableTh('Cond', 'condition')}
                    {renderSortableTh('Print', 'printing')}
                    {renderSortableTh('Qty', 'qty', 'right')}
                    {renderSortableTh('Acq cost', 'acq', 'right')}
                    {renderSortableTh('Current', 'current')}
                    {renderSortableTh('Proposed', 'proposed')}
                    {renderSortableTh('Δ/copy', 'delta')}
                    {renderSortableTh('Line Δ', 'lineDelta', 'right')}
                    <th style={styles.th}>Sell base</th>
                    <th style={styles.th} />
                  </tr>
                </thead>
                <tbody>{pageRows.map((row) => renderRow(row))}</tbody>
              </table>
            </div>
          )}

          {preview && preview.total > allRows.length && (
            <div style={styles.truncation}>
              Loaded {allRows.length} of {preview.total} movers — re-run preview or contact ops if
              truncated.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  summaryBar: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    padding: spacing.sm,
    backgroundColor: colors.gray100,
    borderRadius: borderRadius.sm,
  },
  summaryGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  reviewPending: { color: colors.error, fontWeight: typographyFlat.fontWeight.medium },
  reviewOk: { color: colors.success, fontWeight: typographyFlat.fontWeight.medium },
  toolbarFixed: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
  },
  tableScroll: {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'min(60vh, 720px)',
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.xs,
    alignItems: 'center',
  },
  filterButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
  },
  filterButtonActive: {
    backgroundColor: `${colors.primary}19`,
    borderColor: colors.primary,
    color: colors.primary,
  },
  searchInput: {
    marginLeft: 'auto',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
    minWidth: '180px',
  },
  reviewActions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.xs,
  },
  reviewHint: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    marginRight: spacing.sm,
  },
  pageMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  pageLabel: {
    fontSize: typography.small.fontSize,
    color: colors.text,
    minWidth: '88px',
    textAlign: 'center',
  },
  tableWrap: { overflowX: 'auto', width: '100%' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.small.fontSize,
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.medium,
    whiteSpace: 'nowrap',
  },
  thNum: {
    textAlign: 'right',
  },
  thSortable: {
    padding: 0,
  },
  sortButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: 'none',
    background: 'transparent',
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.medium,
    fontSize: typography.small.fontSize,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textAlign: 'inherit',
  },
  td: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  tdNum: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  dataRow: {},
  dataRowNeedsReview: {
    backgroundColor: `${colors.warning}15`,
  },
  dataRowSkipped: {
    opacity: 0.55,
  },
  cardName: { fontWeight: typographyFlat.fontWeight.medium },
  cardMeta: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  currentCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  newStamp: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  currentHint: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
  chipWarn: {
    fontSize: typographyFlat.fontSize.xs,
    padding: `1px ${spacing.xs}`,
    borderRadius: borderRadius.sm,
    backgroundColor: `${colors.warning}22`,
    color: colors.text,
  },
  chipInfo: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.primary,
  },
  priceInput: {
    width: '72px',
    padding: `${spacing.xs} ${spacing.xs}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
  },
  skipLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    fontSize: typographyFlat.fontSize.xs,
    marginBottom: spacing.xs,
  },
  ackButton: {
    display: 'block',
    padding: `2px ${spacing.xs}`,
    fontSize: typographyFlat.fontSize.xs,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    cursor: 'pointer',
  },
  emptyFilter: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    padding: spacing.md,
    textAlign: 'center',
  },
  truncation: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  primaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: 500,
  },
  primarySmall: {
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
  },
  secondaryButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: colors.gray200,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
  },
  linkButton: {
    padding: 0,
    background: 'none',
    border: 'none',
    color: colors.primary,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
    textDecoration: 'underline',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};
